export const POLICIES = {
    privacy: {
        path: "/privacy-policy",
        titleTag: "Privacy Policy — GrowCo",
        headingLead: "Privacy",
        headingAccent: "policy",
        meta: "Tondku LLP (GrowCo) — Last updated 17/03/2025",
        intro: 'Tondku LLP ("GrowCo") values your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and protect your information when you use our services.',
        sections: [
            {
                title: "Information We Collect",
                intro: "We only collect essential information for providing our services:",
                bullets: [
                    "Payment details for processing transactions.",
                    "Contact form details (Name, Email) to communicate with potential and existing customers.",
                ],
            },
            {
                title: "How We Use Your Information",
                intro: "We use your data solely to:",
                bullets: [
                    "Process payments and subscriptions.",
                    "Communicate with you about services, inquiries, or support requests.",
                    "Improve our services and ensure smooth operations.",
                ],
            },
            {
                title: "Data Sharing",
                intro: "We do not share your personal data with any third parties.",
            },
            {
                title: "Data Retention",
                intro: "We retain customer data only while they are actively using our services.",
                bullets: [
                    "Testimonials provided by customers may be retained indefinitely.",
                    "Users can request data deletion upon canceling services.",
                ],
            },
            {
                title: "Your Rights",
                intro: "You may request to access, update, or delete your personal data at any time by contacting us at ",
                email: true,
            },
            {
                title: "Legal Compliance",
                intro: "We comply with applicable data protection laws, including GDPR, CCPA, and India's IT Act.",
            },
            {
                title: "Contact Us",
                intro: "For any privacy-related concerns, please contact us at ",
                email: true,
            },
        ],
    },
    refund: {
        path: "/cancellation-and-refund-policy",
        titleTag: "Cancellation and Refund Policy — GrowCo",
        headingLead: "Cancellation and refund",
        headingAccent: "policy",
        meta: "Tondku LLP (GrowCo) — Last updated 17/03/2025",
        intro: "We understand that circumstances may change, so we have structured our cancellation and refund policy as follows:",
        sections: [
            {
                title: "Cancellation Policy",
                bullets: [
                    "Customers may cancel their subscription at any time.",
                    "Cancellations must be made at least seven days before renewal to avoid charges for the next billing cycle.",
                    "Customers will continue to receive their full content allocation for the remainder of their billing cycle, even after canceling.",
                ],
            },
            {
                title: "Refund Policy",
                bullets: [
                    "No refunds are provided once a subscription is active.",
                    "Exception: Refunds will be issued only in cases of duplicate charges or billing errors.",
                ],
            },
            {
                title: "Access to Past Work",
                bullets: [
                    "Customers retain access to past content for 30 days from the date of cancellation.",
                    "After 30 days, access will be revoked unless a new subscription is purchased.",
                ],
            },
            {
                title: "Pause Option",
                bullets: [
                    "Customers may request a temporary pause on their subscription instead of canceling.",
                ],
            },
            {
                title: "Dispute Resolution",
                bullets: [
                    "If a customer disputes a charge, we first communicate via email or video chat to clarify the situation.",
                    "If the dispute is valid (e.g., duplicate charges), we issue a full refund for the incorrect charge.",
                    "If the dispute is unjustified, we provide documentation proving the service was delivered.",
                ],
            },
            {
                title: "Contact Us",
                intro: "For cancellation or refund requests, please email ",
                email: true,
            },
        ],
    },
    digital: {
        path: "/digital-services-shipping-policy",
        titleTag: "Digital Services Shipping Policy — GrowCo",
        headingLead: "Digital services shipping",
        headingAccent: "policy",
        meta: "Tondku LLP (GrowCo) — Last updated 17/03/2025",
        intro: 'Tondku LLP ("GrowCo") provides digital services only and does not deal with physical products.',
        sections: [
            {
                title: "Service Delivery",
                bullets: [
                    "Digital assets, including video content, reels, and thumbnails, are delivered through designated platforms (Monday.com, Frame.io).",
                    "Customers will receive content as per their service plan.",
                ],
            },
            {
                title: "Turnaround Time",
                bullets: [
                    "Initial work begins within 2-3 business days after a request.",
                    "Revisions must be requested within 48 hours of delivery, after which the work is considered automatically accepted.",
                ],
            },
            {
                title: "Service Modifications & Customization",
                bullets: [
                    "All service plans are customizable based on the creator's needs.",
                    "GrowCo reserves the right to enhance or modify services at its discretion.",
                ],
            },
            {
                title: "Non-Delivery Policy",
                bullets: [
                    "In the unlikely event that GrowCo is unable to deliver content due to unforeseen circumstances, customers will be notified, and an appropriate solution will be provided.",
                ],
            },
            {
                title: "Contact Us",
                intro: "For any service-related concerns, please contact ",
                email: true,
            },
        ],
    },
    terms: {
        path: "/terms-and-conditions",
        titleTag: "Terms and Conditions — GrowCo",
        headingLead: "Terms and",
        headingAccent: "conditions",
        meta: "Tondku LLP (GrowCo) — Terms of Use — Last updated 29/04/2024",
        sections: [
            {
                title: "Agreement Overview",
                paragraphs: [
                    'This Terms of Use constitutes a legally binding agreement between Tondku LLP ("GrowCo"), and the Customer ("Customer"). Failure to agree to and adhere to all terms, conditions, and obligations contained herein results in the prohibition of the Customer\'s use of the Services, requiring immediate discontinuation. Except for ongoing obligations, such as payment for services rendered, the relationship between Customer and Tondku LLP ("GrowCo") shall cease upon termination of this Agreement.',
                ],
            },
            {
                title: "Service Plans",
                subs: [
                    {
                        title: "Kickoff Kit",
                        paragraphs: [
                            "GrowCo shall deliver up to fifteen reels and two long-forms per calendar month, all basic reels with attractive elements.",
                            "GrowCo retains the right to begin work on new requests within three business days.",
                        ],
                    },
                    {
                        title: "Rise Up",
                        paragraphs: [
                            "This plan assigns 5 more reels, 2 more long-forms and thumbnails with these videos.",
                            "While work hours per week are not guaranteed due to potential interruptions (such as vacations, sick leaves, etc.), GrowCo strives to provide approximately 40 hours of dedicated service per week. Response time for new service items is within two business days.",
                        ],
                    },
                    {
                        title: "Mastery Move",
                        paragraphs: [
                            "Assigns one dedicated animator/editor, and one content strategist. Conditions of the Creator Plan apply, with concurrent work on two requests.",
                        ],
                    },
                ],
                outro: "These Service Plans are subject to modification, enhancement, or revision at GrowCo's sole discretion. Customers should regularly review terms for updates. All the plans are customizable as per the creator needs.",
            },
            {
                title: "Cancellation and Renewal Policy",
                paragraphs: [
                    "Customers must cancel their service at least seven days prior to the renewal date to avoid charges for the subsequent period. Failure to cancel within this timeframe will result in non-refundable charges due to the allocation of resources, such as assigned animators, editors and content strategists.",
                ],
            },
            {
                title: "Automatic Acceptance of Work",
                paragraphs: [
                    "Work delivered to the Customer will be deemed automatically accepted 48 hours after delivery, unless the Customer provides specific feedback or requests revisions within this timeframe. This policy is in place to facilitate the timely commencement of editing the next video, ensuring that Customers receive the highest quantity of content possible within their service plan.",
                ],
            },
            {
                title: "Pricing Plans and Refund Policy",
                paragraphs: [
                    "Customers may cancel their subscriptions at any time. However, as an editor/animator is assigned to the account at the time of creation, no refunds are offered under any circumstances.",
                    'In the event of a cancellation, Tondku LLP ("GrowCo") ensures that the Customer will continue to receive the service as promised until the end of the current billing cycle.',
                    "Please note, that there are no partial month refunds, and the cancellation will take effect from the next billing cycle following the cancellation request.",
                ],
            },
            {
                title: "Intellectual Property Rights",
                paragraphs: [
                    'While Tondku LLP ("GrowCo") retains ownership of the underlying content of the Services, including software, designs, trademarks, logos, audio, video, and general text (collectively, the "Content") that are part of the Services, the specific works created for a Customer as part of the service (e.g., custom videos) are the property of the Customer upon full payment for the respective billing cycle. All such works are protected by copyright and assigned to the Customer, consistent with the laws and regulations of the foreign jurisdictions, and international conventions. This transfer of ownership applies exclusively to completed work paid for by the Customer and does not extend to the general Content owned by Tondku LLP ("GrowCo").',
                ],
            },
            {
                title: "Ownership of Materials",
                paragraphs: [
                    'Consistent with the Intellectual Property Rights section, while Tondku LLP ("GrowCo") retains ownership of its proprietary materials and general content used in the creation process, the Customer obtains full ownership of the final, delivered project work. This includes all design and original source files created specifically for the Customer\'s project.',
                    "Upon full payment for the respective service, the Customer shall be the sole owner of the copyright for all completed projects. It should be noted that this transfer of ownership pertains only to the final, custom-created works and does not include GrowCo's underlying tools, methods, or pre-existing materials.",
                ],
            },
            {
                title: "Fonts",
                paragraphs: [
                    'In the event that any Project incorporates fonts not owned by Tondku LLP ("GrowCo") and requiring a commercial license for legal reproduction, distribution, or public display, Tondku LLP ("GrowCo") will inform the Customer in writing. The Customer will need to purchase the appropriate licenses for the Third-Party Fonts. The Customer assumes all responsibility for consequences due to failure to purchase required licenses for any Third-Party Fonts incorporated into a Project.',
                ],
            },
            {
                title: "Customer Representations",
                paragraphs: [
                    "By using the Services, Customer represents and warrants that: Customer has the legal capacity and agrees to comply with these Terms of Use; Customer is not a minor; Customer will not access the Services through automated or non-human means; Customer will not use the Services for any illegal or unauthorized purpose; Customer's use of the Services will not violate any applicable law or regulation.",
                ],
            },
            {
                title: "Prohibited Activities",
                paragraphs: [
                    'Customers shall use the services provided by Tondku LLP ("GrowCo") solely for lawful purposes and in accordance with the intended use of these services. Specifically, the creative content and materials produced by Tondku LLP ("GrowCo") are to be used by the Customer in a manner that aligns with the agreed-upon project scope and is consistent with the terms of the service plan selected.',
                    "Customers are prohibited from:",
                ],
                bullets: [
                    'Reselling, redistributing, or otherwise exploiting the services or content created by Tondku LLP ("GrowCo") for unauthorized commercial purposes.',
                    "Using the services or content created in any way that infringes upon the intellectual property rights of others, including but not limited to unauthorized reproduction, modification, or distribution.",
                    "Engaging in any activity that is illegal, fraudulent, or violates the rights of any third party.",
                ],
                outro: "The use of Tondku LLP's (\"GrowCo\") services should be in a manner that respects the integrity of the creative process and adheres to the legal and ethical standards of content creation and usage.",
            },
            {
                title: "Feedback",
                paragraphs: [
                    'Customer acknowledges and agrees that any questions, comments, suggestions, or other feedback related to the services provided by Tondku LLP ("GrowCo") must be communicated through designated platforms, specifically Monday.com or Frame.io. This ensures that Feedback is properly recorded and addressed. Tondku LLP ("GrowCo") shall retain ownership of all Feedback and is under no obligation to keep it confidential.',
                    'Tondku LLP ("GrowCo") is not liable for any oversight or failure to act on Feedback that is not shared through the aforementioned designated channels. Customers are encouraged to use these platforms for all communication regarding Feedback to ensure it is duly noted and appropriately responded to.',
                ],
            },
            {
                title: "Oversight",
                paragraphs: [
                    'Tondku LLP ("GrowCo") reserves the right to monitor the Services for violations of these Terms of Use and to take appropriate legal action against violations. Tondku LLP ("GrowCo") further reserves the right to restrict or deny access to the Services or disable the Customer\'s use of the Services, without notice or liability.',
                ],
            },
            {
                title: "Returns and Refunds",
                paragraphs: [
                    'All payments made to Tondku LLP ("GrowCo") for services are non-refundable. This policy is in accordance with the stipulations set forth in the "Pricing Plans and Refund Policy" section, where it is specified that subscriptions may be canceled at any time, but no refunds are offered, regardless of the timing of the cancellation in relation to the billing cycle.',
                    'Tondku LLP ("GrowCo") commits to delivering the promised services until the end of the current billing cycle, following a cancellation request. This no-refund policy ensures operational efficiency and the continued high quality of service provided to all customers.',
                ],
            },
            {
                title: "Modifications to Terms of Use",
                paragraphs: [
                    'Tondku LLP ("GrowCo") reserves the right to change, alter, modify, or remove any content on the Services at its sole discretion. Tondku LLP ("GrowCo") reserves the right to modify or discontinue all or part of the Services without notice and without liability to the Customer.',
                ],
            },
            {
                title: "Interruptions",
                paragraphs: [
                    "There may be occasions where service interruptions occur due to factors beyond our control. These may include, but are not limited to, unforeseen circumstances such as team member unavailability, technical difficulties, or external factors impacting our ability to deliver services.",
                    'In the event of such interruptions, Tondku LLP ("GrowCo") shall not be liable for any loss, damage, or inconvenience caused by the Customer\'s inability to receive the services during these periods. We are committed to minimizing any disruptions and will endeavor to notify Customers of any significant interruptions and resume services as promptly as possible.',
                ],
            },
            {
                title: "Governing Law",
                paragraphs: [
                    "These Terms of Use shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.",
                ],
            },
            {
                title: "Limitations of Liability and Indemnification",
                paragraphs: [
                    'Tondku LLP ("GrowCo"), its directors, employees, members, contractors, or agents shall not be liable for any direct, indirect, consequential, incidental, special, or punitive damages, including lost profit, lost revenue, lost data, or other damages or losses, arising from Customer\'s use of the Services. Customer agrees to defend, indemnify and hold harmless Tondku LLP ("GrowCo") from any loss, damage, liability, claim, or demand due to Customer\'s use of the Services.',
                ],
            },
            {
                title: "Customer Data",
                paragraphs: [
                    'The Customer is responsible for providing all necessary data and materials required for Tondku LLP ("GrowCo") to perform its services. This includes, but is not limited to, specific project requirements, content, images, briefs or any other information relevant to the completion of the services in 48-72 hours.',
                    'While Tondku LLP ("GrowCo") takes reasonable measures to safeguard the data provided by the Customer during the service process, it shall not be liable for any loss, corruption, or compromise of such data. Customers are advised to maintain backups and copies of all materials provided to Tondku LLP ("GrowCo"). In the event of any loss or corruption of data, Tondku LLP ("GrowCo") will endeavor to assist in the recovery process to the best of its ability, but it cannot guarantee the restoration of lost or corrupted data.',
                ],
            },
            {
                title: "Electronic Delivery of Materials",
                paragraphs: [
                    'Customer consents to receive electronic communications from Tondku LLP ("GrowCo") and agrees that all agreements, notices, disclosures, and other communications sent electronically satisfy legal requirements for written communication.',
                ],
            },
            {
                title: "Showcasing Work",
                paragraphs: [
                    'Tondku LLP ("GrowCo") reserves the right to showcase design work on digital channels unless otherwise agreed upon through a Non-Disclosure Agreement (NDA) to protect sensitive information.',
                ],
            },
            {
                title: "Termination",
                paragraphs: [
                    'These Terms of Use remain in effect until terminated by the Customer or Tondku LLP ("GrowCo"). Customers may terminate their agreement following the cancellation policy outlined in the "Pricing Plans and Refund Policy" section.',
                    'Tondku LLP ("GrowCo") reserves the right to terminate this Agreement at any time, with reasonable notice to the Customer, unless in cases of breach by the Customer, including but not limited to non-payment and violation of these Terms of Use.',
                    "Tondku LLP (\"GrowCo\") may also suspend or terminate Customer's access to the Services for any substantial breach of these terms.",
                    "Upon termination, all rights and obligations of both parties, save for those explicitly stated as surviving the termination (such as payment obligations for services already rendered, confidentiality, and ownership of materials), will cease.",
                    'Tondku LLP ("GrowCo") will take reasonable steps to ensure a smooth transition and minimize disruption to the Customer\'s ongoing projects.',
                ],
            },
            {
                title: "Contact Information",
                intro: "For questions or comments regarding these Terms, please contact:",
                emails: ["team@grow-co.net", "ishita@grow-co.net"],
            },
        ],
    },
};
